import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ChevronLeft, ChevronRight, LifeBuoy, LockKeyhole, ShieldCheck, Users, Waves, X } from "lucide-react";
import { useGallery, useTours } from "@/lib/siteData";
import { tourPath } from "@/lib/booking";
import "./JetskiMarketplace.css";

const formats=[
  {id:"jet10",title:"Passeio de jet ski",duration:"10 minutos",price:150,description:"Passeio de 10 minutos com agendamento e confirmação da equipe.",image:"/jetski-gtx.jpg"},
  {id:"jet20",title:"Passeio de jet ski",duration:"20 minutos",price:300,description:"Passeio de 20 minutos com agendamento e confirmação da equipe.",image:"/galeria-3.jpg"},
  {id:"jet30",title:"Passeio de jet ski",duration:"30 minutos",price:450,description:"Passeio de 30 minutos com agendamento e confirmação da equipe.",image:"/galeria-2.jpg"},
  {id:"jet40",title:"Passeio de jet ski",duration:"40 minutos",price:600,description:"Passeio de 40 minutos com agendamento e confirmação da equipe.",image:"/galeria-1.jpg"},
  {id:"jet60",title:"Passeio de jet ski",duration:"1 hora",price:800,description:"Passeio de 1 hora com agendamento e confirmação da equipe.",image:"/jetski-gtx.jpg"}
];
const money=n=>Number(n).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});

// Fotos originais fornecidas pelo proprietário. O site usa as fotos existentes
// até todos os arquivos do pacote serem publicados em /public/jet-paradise.
const PHOTO_ROOT = "/jet-paradise/";
const originalPhoto = (filename) => PHOTO_ROOT + filename;
const newCardPhotos = {
  jet10: originalPhoto("cartao-10-minutos.jpg"),
  jet20: originalPhoto("cartao-20-minutos.jpg"),
  jet30: originalPhoto("cartao-30-minutos.jpg"),
  jet40: originalPhoto("cartao-40-minutos.jpg"),
  jet60: originalPhoto("cartao-1-hora.jpg")
};
const ownerGallery = [
 {id:"orig1",url:originalPhoto("jet-frontal-praia.jpg"),alt:"Sea-Doo GTI SE 170 Roxo Midnight, vista frontal na praia"},
 {id:"orig2",url:originalPhoto("jet-lateral-praia.jpg"),alt:"Sea-Doo GTI SE 170 roxo, perfil lateral em águas rasas"},
 {id:"orig3",url:originalPhoto("jet-lateral-traseira.jpg"),alt:"Sea-Doo GTI SE 170, vista lateral e traseira"},
 {id:"orig4",url:originalPhoto("jet-lateral-paisagem.jpg"),alt:"Sea-Doo GTI SE 170 Roxo Midnight com paisagem tropical"},
 {id:"orig5",url:originalPhoto("jet-traseira-e-som.jpg"),alt:"Sea-Doo GTI SE 170 visto pela traseira e sistema de som"},
 {id:"orig6",url:originalPhoto("jet-frontal-mar.jpg"),alt:"Sea-Doo GTI SE 170 roxo visto de frente com o mar ao fundo"}
];
const ownerDetailPhotos = [
 {url:originalPhoto("jet-detalhe-som.jpg"),alt:"Detalhe do sistema de som BRP no Sea-Doo roxo"},
 {url:originalPhoto("jet-painel-brp.jpg"),alt:"Detalhe do painel e do alto-falante BRP no Sea-Doo GTI SE 170"}
];
function useOwnerPhotosReady() {
 const [ready,setReady] = useState(false);
 useEffect(()=>{
  const controller=new AbortController();
  // O Render pode responder à URL de arquivo inexistente com index.html (SPA).
  fetch(originalPhoto("jet-frontal-praia.jpg"),{method:"HEAD",signal:controller.signal})
   .then(response=>{
     const type=response.headers.get("content-type")||"";
     if(response.ok && type.toLowerCase().includes("image"))setReady(true);
   }).catch(()=>{});
  return ()=>controller.abort();
 },[]);
 return ready;
}

const advantages=[
  {Icon:ShieldCheck,title:"Equipamentos revisados e máxima segurança",desc:"A segurança é prioridade: conferência dos equipamentos e orientações antes do passeio, sempre considerando as condições marítimas e a avaliação da equipe."},
  {Icon:LifeBuoy,title:"Orientação antes do passeio",desc:"A equipe informa as regras de participação, o uso dos equipamentos de segurança e as condições do passeio."},
  {Icon:LockKeyhole,title:"Pagamento organizado",desc:"Opção planejada de sinal de 50% ou pagamento integral via Mercado Pago. A cobrança online será liberada após os testes."},
  {Icon:Users,title:"Atendimento direto",desc:"Dúvidas e confirmação com a equipe pelo WhatsApp da barra inferior do site no celular."}
];
const steps=[
  {n:"1",label:"ESCOLHA SEU JET SKI",desc:"Selecione a duração do passeio: de 10 minutos a 1 hora. O valor é por jet ski."},
  {n:"2",label:"ESCOLHA A DATA E O HORÁRIO",desc:"Confira as opções de agendamento. Na prévia, os horários são demonstrativos e não representam vagas confirmadas."},
  {n:"3",label:"DEFINA O PAGAMENTO",desc:"Escolha sinal de 50% ou pagamento integral. O Mercado Pago será disponibilizado após a homologação."},
  {n:"4",label:"CONFIRME E APROVEITE",desc:"A equipe confirma a disponibilidade e orienta sobre os requisitos, a chegada e as condições para o passeio."}
];
function PhotoLightbox({ photos, index, setIndex, onClose }) {
 const startX = useRef(null);
 const closeRef = useRef(null);
 const count = photos.length;
 useEffect(() => {
  const previousOverflow = document.body.style.overflow;
  const focusedBeforeOpen = document.activeElement;
  document.body.style.overflow = "hidden";
  closeRef.current?.focus();
  const onKey = (event) => {
   if (event.key === "Escape") { event.preventDefault(); onClose(); }
   if (event.key === "ArrowLeft" && count > 1) { event.preventDefault(); setIndex(i => (i - 1 + count) % count); }
   if (event.key === "ArrowRight" && count > 1) { event.preventDefault(); setIndex(i => (i + 1) % count); }
  };
  window.addEventListener("keydown", onKey);
  return () => {
   document.body.style.overflow = previousOverflow;
   window.removeEventListener("keydown", onKey);
   focusedBeforeOpen?.focus?.();
  };
 }, [count, onClose, setIndex]);
 const current = photos[index];
 if (!current) return null;
 const prev = () => setIndex(i => (i - 1 + count) % count);
 const next = () => setIndex(i => (i + 1) % count);
 const onTouchEnd = (event) => {
  if (startX.current === null || count < 2) return;
  const diff = event.changedTouches[0].clientX - startX.current;
  if (Math.abs(diff) > 45) diff > 0 ? prev() : next();
  startX.current = null;
 };
 return createPortal(
  <div className="jp-photo-lightbox" role="dialog" aria-modal="true" aria-label="Galeria de fotos Jet Paradise" onMouseDown={(event)=>{if(event.target===event.currentTarget)onClose();}}>
   <div className="jp-photo-lightbox__top">
    <span aria-live="polite">Foto {index + 1} de {count}</span>
    <button type="button" ref={closeRef} onClick={onClose} aria-label="Fechar foto" className="jp-photo-lightbox__control"><X size={25}/></button>
   </div>
   <div className="jp-photo-lightbox__stage" onTouchStart={e=>{startX.current=e.touches[0].clientX;}} onTouchEnd={onTouchEnd}>
    {count>1&&<button type="button" className="jp-photo-lightbox__control" onClick={prev} aria-label="Foto anterior"><ChevronLeft size={25}/></button>}
    <img src={current.url} alt={current.alt||"Fotografia de jet ski em Ubatuba"} decoding="async"/>
    {count>1&&<button type="button" className="jp-photo-lightbox__control" onClick={next} aria-label="Próxima foto"><ChevronRight size={25}/></button>}
   </div>
   <p className="jp-photo-lightbox__caption">{current.alt||"Galeria Jet Paradise"}</p>
  </div>,
  document.body
 );
}

export default function JetskiMarketplace(){
 const images=useGallery();
 const dbTours=useTours();
 const photoReady=useOwnerPhotosReady();
 const [selected,setSelected]=useState("jet10");
 const [desktop,setDesktop]=useState(()=>window.matchMedia("(min-width: 1024px)").matches);
 const [wideDesktop,setWideDesktop]=useState(()=>window.matchMedia("(min-width: 1280px)").matches);
 const [lightboxIndex,setLightboxIndex]=useState(null);
 useEffect(()=>{
  const desktopQuery=window.matchMedia("(min-width: 1024px)");
  const wideQuery=window.matchMedia("(min-width: 1280px)");
  const sync=()=>{setDesktop(desktopQuery.matches);setWideDesktop(wideQuery.matches);};
  sync();
  if(desktopQuery.addEventListener){desktopQuery.addEventListener("change",sync);wideQuery.addEventListener("change",sync);}
  else {desktopQuery.addListener(sync);wideQuery.addListener(sync);}
  return ()=>{
   if(desktopQuery.removeEventListener){desktopQuery.removeEventListener("change",sync);wideQuery.removeEventListener("change",sync);}
   else {desktopQuery.removeListener(sync);wideQuery.removeListener(sync);}
  };
 },[]);
 // Catálogo existente no Supabase tem precedência: o painel altera preços, imagens e status.
 // Apenas as durações efetivamente oferecidas aparecem. Sem serviço fictício.
 const realTours=dbTours.filter(t=>/^[0-9a-f-]{36}$/i.test(String(t.id)) && t.category==="jetski_passeio" && [10,20,30,40,60].includes(Number(t.duration_minutes)));
 const options=realTours.length ? realTours.map(t=>{
   const minutes=Number(t.duration_minutes);
   const template=formats.find(x=>x.id==="jet"+minutes);
   const now=new Date().toLocaleDateString("sv-SE",{timeZone:"America/Sao_Paulo"});
   const promo=t.promo_price!=null && Number(t.promo_price)>0 && Number(t.promo_price)<Number(t.price) && (!t.promo_starts||t.promo_starts<=now) && (!t.promo_ends||t.promo_ends>=now);
   return {...template,id:"jet"+minutes,title:t.title,description:t.description||template.description,duration:template.duration,
      price:promo?Number(t.promo_price):Number(t.price),originalPrice:promo?Number(t.price):null,
      image:t.image_url||template.image,bookable:Boolean(t.booking_enabled),url:tourPath(t)};
 }).filter(t=>Number.isFinite(t.price)&&t.price>=0).sort((a,b)=>Number(a.id.slice(3))-Number(b.id.slice(3)))
  : dbTours.length===0 ? [] : formats; // fallback original durante carregamento ou indisponibilidade da API
 const chosen=options.find(x=>x.id===selected)||options[0];
 const list=desktop?options:(chosen?[chosen]:[]);
 const onlineEnabled=options.some(x=>x.bookable);
 // Incluir fotos enviadas no painel mesmo quando as fotos originais locais estão prontas.
 // Os 3 registros padrão do Supabase não devem duplicar as fotos já presentes no site.
 const storedGallery=images?.filter(photo=>photo?.url)||[];
 const includedPaths=new Set([
  "/galeria-1.jpg","/galeria-2.jpg","/galeria-3.jpg",
  ...ownerGallery.map(photo=>photo.url),
  originalPhoto("galeria-mar-jet-skis.jpg"),
  originalPhoto("galeria-detalhe-brp.jpg")
 ]);
 const panelAdditions=storedGallery.filter(photo=>{
  try {return !includedPaths.has(new URL(photo.url,window.location.origin).pathname);}
  catch {return !includedPaths.has(photo.url.split("?")[0]);}
 });
 const gallery=[
  ...(photoReady?ownerGallery:storedGallery),
  {id:"jet-galeria-mar",url:originalPhoto("galeria-mar-jet-skis.jpg"),alt:"Dois jet skis nas águas cristalinas de Ubatuba, com montanhas ao fundo"},
  {id:"jet-galeria-brp",url:originalPhoto("galeria-detalhe-brp.jpg"),alt:"Detalhe do Sea-Doo GTI SE 170 Roxo Midnight com sistema de som BRP"},
  ...(photoReady?panelAdditions:[])
 ];
 return <div className="mp-marketplace">
   <section className="mp-section" id="experiencias-jetski">
     <div className="mp-head">
       <div><p className="mp-kicker">Jet Paradise • Ubatuba</p><h2 className="mp-title">Uma experiência de jet ski para cada momento.</h2><p className="mp-intro">Confira nossa tabela de preços por jet ski. Até duas pessoas por equipamento, sujeito à confirmação da equipe. {onlineEnabled?"Consulte somente os horários disponibilizados pela equipe.":"Disponibilidade e agendamento ainda estão em demonstração."}</p></div>
       
     </div>
     <p className="mp-preview-note"><CheckCircle2 size={16}/> {onlineEnabled?"Reservas online sujeitas a disponibilidade confirmada pelo sistema.":"Preços informados pelo Jet Paradise. Horários na prévia são fictícios e não representam vagas abertas."}</p>
     <div className="mp-filter" role="group" aria-label="Filtrar durações" style={desktop?{display:"none"}:undefined}>{options.map(x=><button key={x.id} type="button" className={selected===x.id?"on":""} aria-pressed={selected===x.id} onClick={()=>setSelected(x.id)}>{x.duration}</button>)}</div>
     {!options.length&&<p className="mp-preview-note">Nenhum passeio disponível no catálogo no momento. Consulte a equipe pelo WhatsApp.</p>}
     <div className="mp-product-grid" style={desktop&&wideDesktop?{gridTemplateColumns:"repeat(5,minmax(0,1fr))"}:undefined}>{list.map((product,i)=><article key={product.id} className="mp-product" data-active={selected===product.id}>
      <div className={"mp-photo"+(product.id==="jet40"?" mp-photo--portrait":"")} style={desktop&&product.id==="jet40"?{aspectRatio:"3 / 4",height:"auto",maxHeight:610}:undefined}><img style={desktop&&product.id==="jet40"?{height:"100%",maxHeight:"100%",objectFit:"cover"}:undefined} src={newCardPhotos[product.id]||product.image} onError={(event)=>{ if(event.currentTarget.src.endsWith(product.image)) return; event.currentTarget.src=product.image; }} alt={"Sea-Doo GTI SE 170 Roxo Midnight — "+product.duration} loading="lazy" decoding="async"/><span>{product.duration}</span></div>
      <div className="mp-product-body"><div className="mp-type"><Waves size={14}/> Jet ski • por equipamento</div><h3>{product.title}</h3><p>{product.description}</p>
      <div className="mp-detail"><span>Até 2 participantes, sujeito à avaliação da equipe</span><strong>{money(product.price)} por jet ski</strong></div>
      <Link className="mp-primary" to={product.bookable?product.url:`/demonstracao?experiencia=${product.id}`}><span className="mp-action-label-mobile">{product.bookable?"Ver horários":"Simular passeio"}</span><span className="mp-action-label-desktop">{product.bookable?"Consultar disponibilidade":"Simular esta opção"}</span> <ArrowRight size={17}/></Link></div>
     </article>)}</div>
   </section>
   <section className="mp-section mp-jetski-highlights" id="destaques-jetski" aria-labelledby="mp-highlights-title">
    <h2 id="mp-highlights-title" className="mp-title">Destaques do nosso jet ski:</h2>
    <ul className="mp-highlights-list">
      <li><span aria-hidden="true">🏁</span><span>Motor Rotax de 170 HP.</span></li>
      <li><span aria-hidden="true">💜</span><span>Cor exclusiva Roxo Midnight.</span></li>
      <li><span aria-hidden="true">🔊</span><span>Sistema de som BRP.</span></li>
      <li><span aria-hidden="true">🌊</span><span>Estabilidade e conforto na navegação.</span></li>
      <li><span aria-hidden="true">👥</span><span>Passeios para até duas pessoas por jet ski.</span></li>
      <li><span aria-hidden="true">⚡</span><span>Design esportivo e tecnologia Sea-Doo.</span></li>
    </ul>
    {photoReady&&<div className="mp-equipment-details" aria-label="Detalhes reais do sistema BRP">
      {ownerDetailPhotos.map(photo=><img key={photo.url} src={photo.url} alt={photo.alt} loading="lazy" decoding="async"/>)}
    </div>}
   </section>
   <section className="mp-section mp-how-section" id="como-reservar" aria-labelledby="mp-how-title">
    <div className="mp-heading-rule"><span aria-hidden="true"/><h2 id="mp-how-title">COMO <em>FUNCIONA</em></h2><span aria-hidden="true"/></div>
    <div className="mp-steps">{steps.map(s=><article key={s.n} className="mp-step">
      <span className="mp-step-number" aria-hidden="true">{s.n}</span>
      <h3>{s.label}</h3>
      <p>{s.desc}</p>
    </article>)}</div>
   </section>
   <section className="mp-section mp-why-section" id="sobre-jet-paradise" aria-labelledby="mp-why-title">
    <div className="mp-heading-rule"><span aria-hidden="true"/><h2 id="mp-why-title">POR QUE ESCOLHER O <em>JET PARADISE?</em></h2><span aria-hidden="true"/></div>
    <div className="mp-benefits">{advantages.map(({Icon,title,desc})=><article key={title} className="mp-benefit">
      <Icon size={22} aria-hidden="true" className="mp-benefit-icon"/>
      <h3>{title}</h3><p>{desc}</p>
    </article>)}</div>
   </section>
   {gallery.length>0&&<section className="mp-section" id="fotos-jetski">
    <p className="mp-kicker">Galeria Jet Paradise</p>
    <h2 className="mp-title">Veja nossas imagens</h2>
    <div className="mp-gallery">
      <button type="button" className="mp-gallery-photo mp-gallery-featured" onClick={()=>setLightboxIndex(0)} aria-label="Ampliar foto principal da galeria">
        <img src={gallery[0].url} alt={gallery[0].alt||"Foto principal da galeria Jet Paradise"} loading="lazy" decoding="async"/>
      </button>
      {gallery.length>1&&<div className="mp-gallery-columns">
        {[0,1].map(column=><div className="mp-gallery-column" key={column}>
          {gallery.slice(1).filter((_,i)=>i%2===column).map((photo,i)=>
            <button className="mp-gallery-photo" type="button" key={photo.id||`column-${column}-photo-${i}`} onClick={()=>setLightboxIndex(1+i*2+column)} aria-label={`Ampliar foto ${2+i*2+column} da galeria`}>
              <img src={photo.url} alt={photo.alt||"Foto da galeria Jet Paradise"} loading="lazy" decoding="async"/>
            </button>
          )}
        </div>)}
      </div>}
    </div>
   </section>}
   {lightboxIndex!==null&&<PhotoLightbox photos={gallery} index={lightboxIndex} setIndex={setLightboxIndex} onClose={()=>setLightboxIndex(null)}/>}
 </div>
}
